import { CSSProperties, FC } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { commonUpgrade } from '@rikstv/play-common/src/forces/loadUserInfo/actions';
import { UnstyledLink } from '@rikstv/play-common/src/router/Link';
import { push } from '@rikstv/play-common/src/utils/analytics/googleTagManager';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { H3, H4, SubBody } from '@rikstv/shared-components';

import { PadlockOverlay } from '../../../components/imageOverlays/PadlockOverlay';
import { AppJumpIcon, CartIcon, TvodIcon } from '../../../icons';
import { ProviderState, ProviderVariant } from '../contentStack.types';

import styles from './ProvidersVariant.module.css';

export const ProviderVariantSection = (props: Parameters<typeof ProviderVariantList>[0]) => {
  return (
    <section className={classNames('rds-gutter', styles.providerListSection)}>
      <ProviderVariantList {...props} />
    </section>
  );
};

export const ProviderVariantList: FC<{
  title?: string;
  variants: ProviderVariant[];
  onSelectVariant: (variant: ProviderVariant) => void;
}> = props => {
  return props.variants.length < 1 ? null : <ServiceProviderListInternal {...props} />;
};

const ServiceProviderListInternal: FC<{
  title?: string;
  variants: ProviderVariant[];
  onSelectVariant: (variant: ProviderVariant) => void;
}> = ({ title, variants, onSelectVariant }) => {
  const noAuth = !authService.isAuthenticated();
  const disabledStates: ProviderState[] = ['NoAccess', 'Planned'];

  return (
    <>
      {title && <H3 className={styles.providerListTitle}>{title}</H3>}
      <ol className={styles.providerList}>
        {variants.map(variant => (
          <li
            key={variant.tracking.elementId}
            data-disabled={
              noAuth || (disabledStates.includes(variant.state) && variant.behavior !== 'UpgradeSubscription')
            }
            className={styles.providerListItem}>
            <ServiceProvider variant={variant} onSelectVariant={onSelectVariant} />
          </li>
        ))}
      </ol>
    </>
  );
};

const getVariantLink = (variant: ProviderVariant) => {
  if (variant.behavior === 'PlayExternalAsset') {
    return variant._links.externalPlayback.webPlaybackUri;
  }
  if (variant.behavior === 'ActivateExternalService') {
    return variant._links.externalPlayback.activationPageUrl ?? '';
  }
  return '';
};

const ServiceProvider: FC<{ variant: ProviderVariant; onSelectVariant: (card: ProviderVariant) => void }> = ({
  variant,
  onSelectVariant,
}) => {
  const dispatch = useDispatch();
  const noAccessStates: ProviderState[] = ['NoAccess', 'NotActivated'];
  const isAuthenticated = authService.isAuthenticated();
  const isNoAccess = noAccessStates.includes(variant.state);
  const isPlanned = variant.state === 'Planned';
  const hasAccess = isAuthenticated && !isNoAccess && !isPlanned;
  const externalPlaybackLink = getVariantLink(variant);
  const isTvod = variant.behavior === 'RentAsset';
  const isPurchase = variant.behavior === 'PurchaseAsset';
  const subtitles = variant.subtitle ?? '';
  const grayscaleStyle: CSSProperties = isPlanned || !hasAccess ? { filter: 'grayscale(100%)' } : {};

  return (
    <UnstyledLink
      className={classNames(styles.providerLink, 'with-keyboard-focus')}
      to={externalPlaybackLink}
      onClick={e => {
        if (variant.behavior !== 'ActivateExternalService') {
          e.preventDefault();
          if (hasAccess) {
            onSelectVariant(variant);
          }
        }
        if (variant.behavior === 'UpgradeSubscription') {
          push({
            event: 'user_interaction',
            event_params: {
              context: location.pathname.toString(),
              event_group: 'shopper',
              event_type: 'upgrade from content (variant list)',
            },
          });
          dispatch(commonUpgrade({ fromAssetId: variant.tracking.elementId }));
        }
      }}>
      <div className={styles.providerLogo}>
        <img src={variant.logo.href} alt="" style={grayscaleStyle} />
        {isNoAccess && <PadlockOverlay iconHeight={24} />}
      </div>
      <div>
        <H4 className={styles.providerTitle}>{variant.title}</H4>
        <SubBody className={styles.providerMeta}>{subtitles}</SubBody>
      </div>
      {externalPlaybackLink && <AppJumpIcon className={styles.providerTypeIcon} />}
      {isTvod && <TvodIcon className={styles.providerTypeIcon} />}
      {isPurchase && <CartIcon style={{ marginTop: '-1px' }} className={styles.providerTypeIcon} />}
    </UnstyledLink>
  );
};
